import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, X, Calendar, Clock, MapPin } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { formatDate, pad } from '@/lib/utils';

const empty = {
  court_id: '',
  mode: 'single',           // 'single' | 'range' | 'weekly'
  date: new Date().toISOString().slice(0, 10),
  end_date: new Date().toISOString().slice(0, 10),
  days_of_week: [],         // 0..6 (Mon..Sun)
  start_hour: 18,
  duration_hours: 2,
  reason: '',
};

const DOW_LABELS = [['Mon', 0], ['Tue', 1], ['Wed', 2], ['Thu', 3], ['Fri', 4], ['Sat', 5], ['Sun', 6]];

export default function AdminSlotBlocks() {
  const [courts, setCourts] = useState([]);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ court_id: '', start: '', end: '' });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    const params = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v));
    const { data } = await api.get('/admin/slot-blocks', { params });
    setRows(data);
  };

  useEffect(() => {
    api.get('/courts', { params: { only_active: false } }).then((r) => {
      setCourts(r.data);
      setForm((f) => ({ ...f, court_id: r.data[0]?.id || '' }));
    });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCourt = useMemo(() => courts.find((c) => c.id === form.court_id), [courts, form.court_id]);

  const toggleDow = (d) => {
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(d) ? f.days_of_week.filter((x) => x !== d) : [...f.days_of_week, d],
    }));
  };

  const save = async () => {
    if (!form.court_id) return toast({ title: 'Pick a court', variant: 'error' });
    if (form.mode === 'weekly' && form.days_of_week.length === 0) {
      return toast({ title: 'Pick at least one weekday', variant: 'error' });
    }
    if ((form.mode === 'range' || form.mode === 'weekly') && form.end_date < form.date) {
      return toast({ title: 'End date must be on or after start date', variant: 'error' });
    }
    setBusy(true);
    try {
      const payload = {
        court_id: form.court_id,
        date: form.date,
        start_hour: Number(form.start_hour),
        duration_hours: Number(form.duration_hours),
        reason: form.reason || '',
        mode: form.mode,
        ...(form.mode !== 'single' ? { end_date: form.end_date } : {}),
        ...(form.mode === 'weekly' ? { days_of_week: form.days_of_week } : {}),
      };
      const { data } = await api.post('/admin/slot-blocks', payload);
      toast({
        title: `Blocked ${data.count} slot${data.count === 1 ? '' : 's'}`,
        description: `${selectedCourt?.name} · ${pad(form.start_hour)}:00 – ${pad(form.start_hour + form.duration_hours)}:00`,
        variant: 'success',
      });
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast({ title: 'Block failed', description: e.response?.data?.detail || '', variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Unblock ${label}? This slot will become bookable again.`)) return;
    try {
      await api.delete(`/admin/slot-blocks/${id}`);
      toast({ title: 'Slot unblocked', variant: 'success' });
      load();
    } catch {
      toast({ title: 'Failed to unblock', variant: 'error' });
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6" data-testid="admin-slot-blocks">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Court Availability</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Slot Blocks</h1>
          <p className="text-sm text-white/50 mt-2 max-w-xl">Block any court slot for tournaments, maintenance, weather, private events. Blocked slots disappear from public booking instantly.</p>
        </div>
        <Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="add-block-button"><Plus size={14} /> Block a Slot</Button>
      </div>

      <div className="bg-surface border border-white/10 p-5 grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Field label="Court">
          <select
            value={filter.court_id}
            onChange={(e) => setFilter({ ...filter, court_id: e.target.value })}
            className="w-full h-11 px-4 bg-transparent border border-white/20 text-white focus:border-volt focus:outline-none"
            data-testid="filter-block-court"
          >
            <option value="" className="bg-obsidian">All courts</option>
            {courts.map((c) => <option key={c.id} value={c.id} className="bg-obsidian">{c.name}</option>)}
          </select>
        </Field>
        <Field label="From"><Input type="date" value={filter.start} onChange={(e) => setFilter({ ...filter, start: e.target.value })} /></Field>
        <Field label="To"><Input type="date" value={filter.end} onChange={(e) => setFilter({ ...filter, end: e.target.value })} /></Field>
        <div className="flex items-end"><Button onClick={load} className="w-full" data-testid="apply-block-filters">Apply</Button></div>
      </div>

      <div className="bg-surface border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="border-b border-white/10 bg-obsidian/40">
            <tr className="text-[10px] uppercase tracking-[0.25em] text-white/40">
              <th className="text-left px-5 py-3">Date</th>
              <th className="text-left px-5 py-3">Court</th>
              <th className="text-left px-5 py-3">Time</th>
              <th className="text-left px-5 py-3">Reason</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan="6" className="px-5 py-12 text-center text-white/40">No slot blocks. Court schedule is fully open.</td></tr>
            ) : rows.map((b) => {
              const past = b.date < today;
              return (
                <tr key={b.id} className={`border-b border-white/5 hover:bg-white/5 ${past ? 'opacity-50' : ''}`} data-testid={`block-row-${b.id}`}>
                  <td className="px-5 py-3 text-white/80">{formatDate(b.date)}</td>
                  <td className="px-5 py-3 text-white/80">{b.court_name}</td>
                  <td className="px-5 py-3 font-mono text-xs">{pad(b.start_hour)}:00 – {pad(b.start_hour + b.duration_hours)}:00</td>
                  <td className="px-5 py-3 text-white/60 text-xs">{b.reason || '—'}</td>
                  <td className="px-5 py-3"><Badge tone={past ? 'default' : 'warning'}>{past ? 'Past' : 'Active'}</Badge></td>
                  <td className="px-5 py-3 text-right">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => remove(b.id, `${b.court_name} · ${b.date} · ${pad(b.start_hour)}:00`)}
                      data-testid={`unblock-${b.id}`}
                    >
                      <Trash2 size={12} /> Unblock
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-surface border border-white/10 max-w-lg w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="block-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl uppercase">Block a Court Slot</h2>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <Field label="Court">
                <select
                  value={form.court_id}
                  onChange={(e) => setForm({ ...form, court_id: e.target.value })}
                  className="w-full h-11 px-4 bg-transparent border border-white/20 text-white focus:border-volt focus:outline-none"
                  data-testid="block-court-select"
                >
                  {courts.map((c) => <option key={c.id} value={c.id} className="bg-obsidian">{c.name} ({pad(c.open_hour)}:00–{pad(c.close_hour)}:00)</option>)}
                </select>
              </Field>

              <Field label="Mode">
                <div className="grid grid-cols-3 gap-2">
                  {[
                    ['single', 'Single day'],
                    ['range', 'Date range'],
                    ['weekly', 'Weekly pattern'],
                  ].map(([k, l]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setForm({ ...form, mode: k })}
                      className={`px-3 py-3 text-xs font-bold uppercase tracking-wider border ${form.mode === k ? 'border-volt text-volt bg-volt/5' : 'border-white/15 text-white/60 hover:border-white/30'}`}
                      data-testid={`block-mode-${k}`}
                    >{l}</button>
                  ))}
                </div>
              </Field>

              {form.mode === 'single' ? (
                <Field label="Date">
                  <Input type="date" min={today} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="block-date" />
                </Field>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Start date">
                    <Input type="date" min={today} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="block-date" />
                  </Field>
                  <Field label="End date">
                    <Input type="date" min={form.date} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="block-end-date" />
                  </Field>
                </div>
              )}

              {form.mode === 'weekly' && (
                <Field label="Days of week">
                  <div className="flex flex-wrap gap-2">
                    {DOW_LABELS.map(([lbl, d]) => {
                      const active = form.days_of_week.includes(d);
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => toggleDow(d)}
                          className={`w-12 h-10 text-xs font-bold uppercase tracking-wider border ${active ? 'border-volt text-volt bg-volt/10' : 'border-white/15 text-white/60 hover:border-white/30'}`}
                          data-testid={`block-dow-${d}`}
                        >{lbl}</button>
                      );
                    })}
                  </div>
                </Field>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Start hour">
                  <select
                    value={form.start_hour}
                    onChange={(e) => setForm({ ...form, start_hour: Number(e.target.value) })}
                    className="w-full h-11 px-4 bg-transparent border border-white/20 text-white focus:border-volt focus:outline-none"
                    data-testid="block-start-hour"
                  >
                    {Array.from({ length: 24 }).map((_, h) => {
                      const valid = !selectedCourt || (h >= selectedCourt.open_hour && h + form.duration_hours <= selectedCourt.close_hour);
                      return <option key={h} value={h} disabled={!valid} className="bg-obsidian">{pad(h)}:00</option>;
                    })}
                  </select>
                </Field>
                <Field label="Duration (hours)">
                  <Input type="number" min="1" max="12" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: Number(e.target.value) })} data-testid="block-duration" />
                </Field>
              </div>
              <Field label="Reason (optional)">
                <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Maintenance / Tournament / Weather …" data-testid="block-reason" />
              </Field>
              <div className="bg-obsidian border border-white/10 p-4 text-xs">
                <div className="text-white/40 uppercase tracking-[0.25em] mb-2">Preview</div>
                <div className="text-white"><MapPin size={12} className="inline mr-1 text-volt" /> {selectedCourt?.name || '—'}</div>
                {form.mode === 'single' && <div className="text-white mt-1"><Calendar size={12} className="inline mr-1 text-volt" /> {form.date}</div>}
                {form.mode === 'range' && <div className="text-white mt-1"><Calendar size={12} className="inline mr-1 text-volt" /> {form.date} → {form.end_date} (every day)</div>}
                {form.mode === 'weekly' && <div className="text-white mt-1"><Calendar size={12} className="inline mr-1 text-volt" /> {form.date} → {form.end_date} on {form.days_of_week.map((d) => DOW_LABELS.find(([, x]) => x === d)?.[0]).join(', ') || '—'}</div>}
                <div className="text-white mt-1"><Clock size={12} className="inline mr-1 text-volt" /> {pad(form.start_hour)}:00 – {pad(form.start_hour + form.duration_hours)}:00</div>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={save} disabled={busy} className="flex-1" data-testid="save-block">{busy ? 'Saving…' : 'Block Slot'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
